import {useEffect} from 'react';
import {Viewer} from 'bigdatr-style';

type ChangeTeam = (teamId: string, options?: {redirectToHome?: boolean}) => void;

export function getUrlRequestedTeam(locationSearch: string) {
    return new URLSearchParams(locationSearch).get('currentTeam');
}

export function getSearchWithoutUrlRequestedTeam(locationSearch: string) {
    const params = new URLSearchParams(locationSearch);
    params.delete('currentTeam');

    const nextSearch = params.toString();
    return nextSearch ? `?${nextSearch}` : '';
}

export function usePermissionFallbackTeamSwitch(input: {
    changeTeam: ChangeTeam;
    isViewerRequestSuccess: boolean;
    shouldSwitchToUrlTeam: boolean;
    teamId: string;
    viewer: Viewer | undefined;
    locationSearch: string;
    replaceLocationSearch: (nextSearch: string) => void;
}) {
    const {
        changeTeam,
        isViewerRequestSuccess,
        shouldSwitchToUrlTeam,
        teamId,
        viewer,
        locationSearch,
        replaceLocationSearch
    } = input;
    const urlRequestedTeam = getUrlRequestedTeam(locationSearch);

    useEffect(() => {
        if (shouldSwitchToUrlTeam) return;

        if (isViewerRequestSuccess && viewer) {
            // Respect the URL team only when it resolves to the active team and that team has
            // product access. Invalid or no-permission URL teams are ignored so country can apply.
            const shouldPreserveUrlRequestedTeam =
                urlRequestedTeam === viewer.currentTeamId && !viewer.currentTeam.hasNoPermissions;
            if (shouldPreserveUrlRequestedTeam) return;

            if (urlRequestedTeam) {
                replaceLocationSearch(getSearchWithoutUrlRequestedTeam(locationSearch));
            }

            const teamIdWithPermissions = Viewer.getTeamIdWithPermissions({
                prefferedTeamId: teamId,
                teams: viewer.teams
            });

            // if currentTeamId does not have permissions, automatically switch the user to a team
            // that has permissions
            if (teamIdWithPermissions && viewer.currentTeamId !== teamIdWithPermissions) {
                if (urlRequestedTeam) {
                    changeTeam(teamIdWithPermissions, {redirectToHome: false});
                    return;
                }

                changeTeam(teamIdWithPermissions);
            }
        }
    }, [
        changeTeam,
        isViewerRequestSuccess,
        shouldSwitchToUrlTeam,
        teamId,
        urlRequestedTeam,
        viewer,
        locationSearch,
        replaceLocationSearch
    ]);
}
